/* eslint-disable no-console */
import {Col, Form, Select} from 'antd';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import React, {useEffect, useState} from 'react';
import {useSearchParams} from 'react-router-dom';

import {ReactComponent as YearCalender} from '../../assets/yearCalender.svg';
import {CommonErrors, TF} from '../../constants/Common';
import InformativeText from '../../constants/InformativeText';
import {Itabs} from '../../interface/Tab';
import TranslatedText from '../../libraries/I18N/TranslatedText';
import {sortBy} from '../../shared/commonFunctions';
import useCompanyStore from '../../store/companyStore/companyDetailsStore';
import {yearSelect} from '../../utils/FormUtils/yearSelect';
import Loader from '../Loader/Loader';
import {MYConfig, MYData} from '../MonthlyYearly/Interface/common';
import MonthlyYearlyDrawerForm from '../MonthlyYearly/MonthlyYearlyDrawerForm';
import Tabs from '../Tabs/Tabs';

import {yearKeyLabel} from './Constants/IP/commonKeyLabel';
import {EoLTKey, EoLTLabel} from './Constants/Products/EotKeyLabel';
import {filterSelectOptions} from './Utils/commonFunctions';

const EoLTForm: React.FC<{
  rawMaterialFilter: any;
  currentStep?: number;
  setFormSelectedYear: React.Dispatch<React.SetStateAction<string>>;
  setIsFormLoading: React.Dispatch<React.SetStateAction<boolean>>;
  isFormLoading: boolean;
}> = ({
  rawMaterialFilter,
  currentStep,
  setFormSelectedYear,
  setIsFormLoading,
  isFormLoading
}) => {
  const form = useFormInstance();
  const [searchParam] = useSearchParams();
  const {setFields, getFieldValue} = form;
  const companyUnits = useCompanyStore.getState().preUnits;

  const {facilityFilter, productFilter} = rawMaterialFilter;
  const [resetIn, setResetIn] = useState(0);

  const eoltInfo = InformativeText?.Product?.eolt;

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (resetIn === 0) {
      timer = setTimeout(() => {
        setResetIn(resetIn + 1);
      }, 1);
    }

    return () => {
      clearTimeout(timer);
    };
  }, [resetIn]);

  const yearFilter = yearSelect();

  const monYear: MYConfig[] = [
    {
      mainTitle: {key: EoLTKey.QUANTITY_SOLD, title: EoLTLabel.QUANTITY_SOLD},
      unit: companyUnits.weight
    }
  ];

  const treated: MYConfig[] = [
    {
      mainTitle: {
        key: EoLTKey.treated.productTreated,
        title: EoLTLabel.treated.productTreated
      },
      unit: companyUnits.weight
    }
  ];

  const notTreated: MYConfig[] = [
    {
      mainTitle: {
        key: EoLTKey.notTreated.ProductNotTreated,
        title: EoLTLabel.notTreated.ProductNotTreated
      },
      unit: companyUnits.weight
    }
  ];

  const wasteMethod: Itabs[] = [
    {
      id: EoLTKey.disposalKey.landfill,
      name: (
        <TranslatedText
          translationKey={EoLTLabel.disposalKey.landfill}
          tooltip={eoltInfo?.landfill}
        />
      ),
      component: (
        <MonthlyYearlyDrawerForm
          config={treated}
          data={
            getFieldValue(EoLTKey.disposalMethodData)?.find((item: any) => {
              return item?.disposalMethod === EoLTKey.disposalKey.landfill;
            })?.[TF]
          }
          disposalMethod={{
            value: true,
            type: EoLTKey.disposalKey.landfill,
            key: EoLTKey.disposalMethod
          }}
          nested={EoLTKey.disposalMethodData}
        />
      ),
      forceRender: true
    },
    {
      id: EoLTKey.disposalKey.incineration,
      name: (
        <TranslatedText
          translationKey={EoLTLabel.disposalKey.incineration}
          tooltip={eoltInfo?.incineration}
        />
      ),
      component: (
        <MonthlyYearlyDrawerForm
          config={treated}
          data={
            (getFieldValue(EoLTKey.disposalMethodData) ?? [])?.find(
              (item: any) => {
                return (
                  item?.disposalMethod === EoLTKey.disposalKey.incineration
                );
              }
            )?.[TF] as MYData[]
          }
          disposalMethod={{
            value: true,
            type: EoLTKey.disposalKey.incineration,
            key: EoLTKey.disposalMethod
          }}
          nested={EoLTKey.disposalMethodData}
        />
      ),
      forceRender: true
    },
    {
      id: EoLTKey.disposalKey.recycling,
      name: (
        <TranslatedText
          translationKey={EoLTLabel.disposalKey.recycling}
          tooltip={eoltInfo?.recycle}
        />
      ),
      component: (
        <MonthlyYearlyDrawerForm
          config={treated}
          data={
            (getFieldValue(EoLTKey.disposalMethodData) ?? [])?.find(
              (item: any) => {
                return item?.disposalMethod === EoLTKey.disposalKey.recycling;
              }
            )?.[TF] as MYData[]
          }
          disposalMethod={{
            value: true,
            type: EoLTKey.disposalKey.recycling,
            key: EoLTKey.disposalMethod
          }}
          nested={EoLTKey.disposalMethodData}
        />
      ),
      forceRender: true
    },
    {
      id: EoLTKey.disposalKey.reclaimed,
      name: (
        <TranslatedText
          translationKey={EoLTLabel.disposalKey.reclaimed}
          tooltip={eoltInfo?.reclaimed}
        />
      ),
      component: (
        <MonthlyYearlyDrawerForm
          config={treated}
          data={
            (getFieldValue(EoLTKey.disposalMethodData) ?? [])?.find(
              (item: any) => {
                return item?.disposalMethod === EoLTKey.disposalKey.reclaimed;
              }
            )?.[TF] as MYData[]
          }
          disposalMethod={{
            value: true,
            type: EoLTKey.disposalKey.reclaimed,
            key: EoLTKey.disposalMethod
          }}
          nested={EoLTKey.disposalMethodData}
        />
      ),
      forceRender: true
    },
    {
      id: EoLTKey.disposalKey.reused,
      name: (
        <TranslatedText
          translationKey={EoLTLabel.disposalKey.reused}
          tooltip={eoltInfo?.reuse}
        />
      ),
      component: (
        <MonthlyYearlyDrawerForm
          config={treated}
          data={
            (getFieldValue(EoLTKey.disposalMethodData) ?? [])?.find(
              (item: any) => {
                return item?.disposalMethod === EoLTKey.disposalKey.reused;
              }
            )?.[TF] as MYData[]
          }
          disposalMethod={{
            value: true,
            type: EoLTKey.disposalKey.reused,
            key: EoLTKey.disposalMethod
          }}
          nested={EoLTKey.disposalMethodData}
        />
      ),
      forceRender: true
    },
    {
      id: EoLTKey.disposalKey.notTreated,
      name: (
        <TranslatedText translationKey={EoLTLabel.disposalKey.notTreated} />
      ),
      component: (
        <MonthlyYearlyDrawerForm
          config={notTreated}
          data={
            (getFieldValue(EoLTKey.disposalMethodData) ?? [])?.find(
              (item: any) => {
                return item?.disposalMethod === EoLTKey.disposalKey.notTreated;
              }
            )?.[TF] as MYData[]
          }
          disposalMethod={{
            value: true,
            type: EoLTKey.disposalKey.notTreated,
            key: EoLTKey.disposalMethod
          }}
          nested={EoLTKey.disposalMethodData}
        />
      ),
      forceRender: true
    }
  ];

  return (
    <>
      {isFormLoading ? (
        <Loader />
      ) : (
        <>
          {currentStep === 0 && (
            <>
              <Col className="gutter-row">
                <Form.Item
                  name={yearKeyLabel.key}
                  label={yearKeyLabel.label}
                  labelCol={{span: 24}}
                  initialValue={searchParam.get('selectedYear')}
                >
                  <Select
                    placeholder={yearKeyLabel.label}
                    options={yearFilter}
                    suffixIcon={<YearCalender />}
                    disabled={getFieldValue('_id') !== undefined}
                    onChange={(e) => {
                      setFormSelectedYear(e);
                      setIsFormLoading(true);

                      if (getFieldValue(EoLTKey.PRODUCT_ID)) {
                        setFields([
                          {
                            name: EoLTKey.PRODUCT_ID,
                            errors: ['Please select product again'],
                            value: undefined
                          }
                        ]);
                      }

                      if (getFieldValue(EoLTKey.FACILITY)) {
                        setFields([
                          {
                            name: EoLTKey.FACILITY,
                            errors: ['Please select facility again'],
                            value: undefined
                          }
                        ]);
                      }
                    }}
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row">
                <Form.Item
                  name={EoLTKey.PRODUCT_ID}
                  rules={[
                    {
                      required: true,
                      message: CommonErrors.mandatory(EoLTLabel.PRODUCT_ID)
                    }
                  ]}
                  label={EoLTLabel.PRODUCT_ID}
                  labelCol={{span: 24}}
                >
                  <Select
                    showSearch
                    filterOption={filterSelectOptions}
                    className="select"
                    placeholder={EoLTLabel.PRODUCT_ID}
                    options={sortBy(productFilter, 'label', 'asc')}
                    disabled={getFieldValue('_id') !== undefined}
                    onChange={() => {
                      if (getFieldValue(EoLTKey.FACILITY)) {
                        setFields([
                          {
                            name: EoLTKey.FACILITY,
                            errors: [CommonErrors.selectAgain],
                            value: undefined
                          }
                        ]);
                      }
                    }}
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row">
                <Form.Item dependencies={[EoLTKey.PRODUCT_ID]} noStyle>
                  {({getFieldValue}) => {
                    return (
                      <Form.Item
                        name={EoLTKey.FACILITY}
                        rules={[
                          {
                            required: true,
                            message: CommonErrors.mandatory(EoLTLabel.FACILITY)
                          }
                        ]}
                        label={EoLTLabel.FACILITY}
                        labelCol={{span: 24}}
                      >
                        <Select
                          showSearch
                          filterOption={filterSelectOptions}
                          className="select"
                          placeholder={EoLTLabel.FACILITY}
                          options={sortBy(
                            facilityFilter?.filter((item: any) => {
                              return (
                                item?.[EoLTKey.PRODUCT_ID] ===
                                getFieldValue(EoLTKey.PRODUCT_ID)
                              );
                            }),
                            'label',
                            'asc'
                          )}
                          disabled={getFieldValue('_id') !== undefined}
                        />
                      </Form.Item>
                    );
                  }}
                </Form.Item>
              </Col>
              <MonthlyYearlyDrawerForm
                config={monYear}
                data={getFieldValue(TF) as MYData[]}
              />
            </>
          )}

          {currentStep === 1 && (
            <>
              <Tabs
                array={wasteMethod}
                type="card"
                destroy={false}
                slide={true}
              />
            </>
          )}
        </>
      )}
    </>
  );
};

export default EoLTForm;
